// Stocktake — total_data catalog + count-row helpers.
import axios from "axios";
import {
  BASE_URL,
  BASE_NODE_URL,
  STOCKTAKE_TOTAL_PRODUCTS_URL,
  STOCKTAKE_SAVE_COUNTS,
  STOCKTAKE_GET_DETAILS,
  STOCKTAKE_UPDATE_RECOUNT_FLAG,
  STOCKTAKE_POST_ADJUSTMENT,
  STOCKTAKE_VOID_STOCKTAKE,
  STOCKTAKE_REMOVE_ITEM,
  STOCKTAKE_DELETE,
  STOCKTAKE_EMAIL,
  NEW_PURCHASE_ORDER_PRODUCT_LIST,
  NEW_PURCHASE_ORDER_PRODUCT_BY_UPC,
} from "../../../../Constants/Config";
import { resolveProductListBaseUrl } from "../../../../services/purchaseOrders/purchaseOrdersApi";
import { exportRawCsvToNative } from "../../../../helperFunctions/exportReport";

/** Auth fields required on Stocktake New UI API requests. */
function stocktakeAuthFields({
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
} = {}) {
  return {
    token_id: tokenId ?? "",
    login_type: loginType ?? "",
    session_id: sessionId ?? "",
    login_employee_id:
      loginEmployeeId != null && loginEmployeeId !== ""
        ? String(loginEmployeeId)
        : "",
  };
}

function appendStocktakeAuthToFormData(formData, auth = {}) {
  Object.entries(stocktakeAuthFields(auth)).forEach(([key, value]) => {
    formData.append(key, String(value ?? ""));
  });
  return formData;
}

function withStocktakeAuth(body, auth = {}) {
  return { ...body, ...stocktakeAuthFields(auth) };
}

const STOCKTAKE_COLORS = [
  "var(--qv-tx-0a64f9, #0A64F9)",
  "var(--qv-tx-2f9e44, #2f9e44)",
  "var(--qv-tx-e67700, #e67700)",
  "var(--qv-tx-7048e8, #7048e8)",
  "var(--qv-tx-0c8599, #0c8599)",
  "var(--qv-tx-c92a2a, #c92a2a)",
  "var(--qv-tx-862e9c, #862e9c)",
  "var(--qv-tx-495057, #495057)",
];

const ST_STATUS_META = {
  "in-progress": {
    label: "In progress",
    color: "var(--qv-tx-0850cc, #0850CC)",
    bg: "var(--qv-bg-e7f0ff, #e7f0ff)",
    dot: "#0A64F9",
  },
  draft: {
    label: "Draft",
    color: "var(--qv-tx-0850cc, #0850CC)",
    bg: "var(--qv-bg-e7f0ff, #e7f0ff)",
    dot: "#0A64F9",
  },
  review: {
    label: "Ready to post",
    color: "var(--qv-tx-a14a00, #a14a00)",
    bg: "var(--qv-bg-fff1de, #fff1de)",
    dot: "#e8590c",
  },
  completed: {
    label: "Posted",
    color: "var(--qv-tx-2b6e3f, #2b6e3f)",
    bg: "var(--qv-bg-e8f5ed, #e8f5ed)",
    dot: "#2b8a3e",
  },
  void: {
    label: "Void",
    color: "var(--qv-tx-495057, #495057)",
    bg: "var(--qv-bg-f4f5f7, #f4f5f7)",
    dot: "#868e96",
  },
};

/** API status: 0=Posted, 1=In Progress, 2=Void, 3=Ready to Post */
const STOCKTAKE_API_STATUS_BY_CODE = {
  0: "completed",
  1: "in-progress",
  2: "void",
  3: "review",
};

const STOCKTAKE_API_STATUS_BY_LABEL = {
  "in progress": "in-progress",
  draft: "in-progress",
  "ready to post": "review",
  posted: "completed",
  completed: "completed",
  void: "void",
};

/** Normalize stocktake_list row status → UI key (in-progress | review | completed | void). */
function normalizeStocktakeStatus(row) {
  const code = row?.status != null ? String(row.status).trim() : "";
  if (code in STOCKTAKE_API_STATUS_BY_CODE) {
    return STOCKTAKE_API_STATUS_BY_CODE[code];
  }
  const label = (row?.status_label || "").trim().toLowerCase();
  if (label && STOCKTAKE_API_STATUS_BY_LABEL[label]) {
    return STOCKTAKE_API_STATUS_BY_LABEL[label];
  }
  return "in-progress";
}

/** Map list filter tab → stocktake_list API status param. */
function mapListFilterToApiStatus(filter) {
  switch (filter) {
    case "in-progress":
      return "1";
    case "review":
      return "3";
    case "completed":
      return "0";
    case "void":
      return "2";
    default:
      return "all";
  }
}

function isPostedOrClosedStocktakeStatus(status) {
  return status === "completed" || status === "void";
}

/**
 * Category/vendor counts page against the full scoped pool, so the item
 * total is unreliable — the list shows "N counted" with no progress bar.
 */
function stocktakeListHidesProgressBar(session) {
  return session?.scope === "category" || session?.scope === "vendor";
}

/** List Progress column — always "N counted" for all scopes.
 * Bar + "counted/items" fraction kept below for further discussion. */
function formatStocktakeListProgress(session) {
  const counted = Number(session?.counted) || 0;
  const items = Number(session?.items) || 0;
  const status = session?.status;

  if (isPostedOrClosedStocktakeStatus(status) && counted === 0 && items === 0) {
    return "—";
  }
  // Previous: bar + fraction for walkaround; "N counted" only for category/vendor.
  // if (stocktakeListHidesProgressBar(session)) {
  //   const n = isPostedOrClosedStocktakeStatus(status)
  //     ? counted || items
  //     : counted;
  //   return `${n} counted`;
  // }
  // return `${counted}/${items}`;
  const n = isPostedOrClosedStocktakeStatus(status)
    ? counted || items
    : counted;
  return `${n} counted`;
}

/** Void action is only allowed after a stocktake has been posted. */
function canVoidStocktakeStatus(status) {
  return status === "completed";
}

/** Email report is available for posted stocktakes (same as legacy UI). */
function canEmailStocktakeStatus(status) {
  return status === "completed";
}

const ST_SCOPE_META = {
  walkaround: { label: "Walkaround", icon: "route" },
  category: { label: "Category", icon: "category" },
  vendor: { label: "Vendor", icon: "truck" },
  // zone: { label: "Zone", icon: "pin" },
  // cycle: { label: "Cycle count", icon: "history" },
  adhoc: { label: "Ad-hoc", icon: "shuffle" },
  // full: { label: "Full count", icon: "inventory" },
};

/** Scope card label used when the user leaves the count name blank. */
export function resolveStocktakeDefaultScopeName(scope) {
  return ST_SCOPE_META[scope]?.label || scope;
}

/** e.g. Walkaround — 6/17/2026 */
export function buildDefaultStocktakeName(scopeDisplayName, date = new Date()) {
  const label = String(scopeDisplayName || "").trim() || "Count";
  const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  return `${label} — ${formatted}`;
}

/** API scope_type values for create/list endpoints. */
export const STOCKTAKE_API_SCOPE_TYPES = [
  "walkaround",
  "category",
  "vendor",
  "zone",
  "cycle_count",
  "adhoc",
  "full",
];

const UI_SCOPE_TO_API_SCOPE = { cycle: "cycle_count" };
const API_SCOPE_TO_UI_SCOPE = { cycle_count: "cycle" };

export function mapUiScopeToApiScopeType(uiScope) {
  const key = String(uiScope || "").trim();
  return UI_SCOPE_TO_API_SCOPE[key] || key;
}

export function mapApiScopeTypeToUi(apiScope) {
  const key = String(apiScope || "")
    .trim()
    .toLowerCase();
  // Legacy/null scope_type rows are treated as walkaround counts.
  if (!key) return "walkaround";
  return API_SCOPE_TO_UI_SCOPE[key] || key;
}

/**
 * Normalize create_stocktake / get_stocktake_details `result` to
 * `{ stocktake, items }` (supports nested shape and legacy flat header).
 */
export function normalizeStocktakeDetailsResult(result) {
  if (!result || typeof result !== "object") return null;
  if (result.stocktake && typeof result.stocktake === "object") {
    const allItems = normalizeStocktakeAllItems(result.stocktake.all_items) || [];
    const items = Array.isArray(result.items) ? result.items : [];
    return {
      stocktake: result.stocktake,
      items: enrichDetailItemsProductNamesFromAllItems(items, allItems),
    };
  }
  // Legacy flat create response (header fields on result itself).
  if (result.id != null || result.st_id != null || result.st_code != null) {
    return { stocktake: result, items: [] };
  }
  return null;
}

/** Map create_stocktake API result → session for StocktakeSession. */
export function mapCreateStocktakeResultToSession(row, extras = {}) {
  const scope = mapApiScopeTypeToUi(row?.scope_type);
  const items = parseInt(row?.total_items, 10) || 0;
  const status = normalizeStocktakeStatus(row);
  const stocktakeId = row?.st_id || String(row?.id ?? "");

  return {
    id: stocktakeId,
    apiId: String(row?.id ?? ""),
    name: row?.name?.trim() || extras.name?.trim() || stocktakeId,
    scope,
    scopeLabel:
      row?.scope_name?.trim() ||
      extras.scopeLabel ||
      ST_SCOPE_META[scope]?.label ||
      scope,
    ...(extras.scopeFilterIds
      ? { scopeFilterIds: String(extras.scopeFilterIds).trim() }
      : row?.scope_id
        ? { scopeFilterIds: String(row.scope_id).trim() }
        : {}),
    counter:
      row?.employee_name?.trim() ||
      (row?.employee_id && row?.employee_id !== "0"
        ? `Employee #${row.employee_id}`
        : // employee_id "0" with no name means the merchant admin ran the count.
        "Admin"),
    started: row?.created_at || "",
    created: row?.created_at || "",
    updated:
      row?.last_updated_on || row?.updated_at || row?.created_at || "",
    status,
    statusLabel:
      row?.status_label?.trim() || ST_STATUS_META[status]?.label || "",
    items,
    counted: 0,
    variance: { units: 0, value: 0, lossUnits: 0, gainUnits: 0 },
    openScan: scope === "walkaround",
    allItems: normalizeStocktakeAllItems(row?.all_items) || [],
    raw: row,
  };
}

/**
 * all_items may arrive as a real JSON array or as an object keyed by index
 * ({"0": {...}, "1": {...}} — PHP associative-array encoding). Normalize to
 * an array; returns null when the value is not an item list at all.
 */
export function normalizeStocktakeAllItems(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return null;
}

/**
 * Numeric stocktake_item_id, or null when the row has never been saved.
 * Number(null) / Number("") are 0, so an unsaved row would otherwise sort
 * ahead of every saved one.
 */
function parseStocktakeItemSortId(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse stocktake_items.sort_seq — the stored screen position, 0 = top.
 *
 * Arrives as a STRING: the API's response middleware coerces every number, so
 * this sees "0", not 0. A never-saved row has null/undefined and gets no
 * position; 0 is a real position and must survive.
 */
function parseStocktakeSortSeq(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Stamp each row with its current on-screen position, top-down.
 *
 * save_counts persists this as sort_seq, which is what makes the order survive
 * save -> leave -> reopen. It cannot be inferred from the auto-increment id:
 * the upsert keys on the primary key, so a row that is re-counted or re-scanned
 * keeps the id it was first inserted with.
 */
function assignStocktakeSortSeq(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => ({ ...row, sortSeq: index }));
}

/** Compare by saved item id (ascending), unsaved last, original order as tie-break. */
function compareByStocktakeItemId(aId, bId, aIndex, bIndex) {
  if (aId !== null && bId !== null && aId !== bId) return aId - bId;
  if ((aId === null) !== (bId === null)) return aId === null ? 1 : -1;
  return aIndex - bIndex;
}

/**
 * Compare by saved item id (descending) so the most recently counted row sorts
 * first. This is the legacy fallback only: every row the client saves now
 * carries an explicit sort_seq, so it applies to rows written before that
 * column existed, where the payload went out oldest-counted-first and a higher
 * id therefore meant a later count. An unsaved row is newer than every saved
 * id, so it leads.
 */
function compareByStocktakeItemIdDesc(aId, bId, aIndex, bIndex) {
  if (aId !== null && bId !== null && aId !== bId) return bId - aId;
  if ((aId === null) !== (bId === null)) return aId === null ? -1 : 1;
  return aIndex - bIndex;
}

/**
 * Put detail items into the order the merchant last saw.
 *
 * sort_seq wins where it exists (ascending, 0 = top). Items saved before that
 * column existed have none, and fall back to the old rule: ascending id, which
 * undoes the newest-first order the API returns for them.
 */
export function sortStocktakeDetailItemsByEntryOrder(items) {
  if (!Array.isArray(items) || items.length < 2) return items || [];

  const hasSortSeq = items.some(
    (item) => parseStocktakeSortSeq(item?.sort_seq) !== null,
  );

  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (hasSortSeq) {
        const aSeq = parseStocktakeSortSeq(a.item?.sort_seq);
        const bSeq = parseStocktakeSortSeq(b.item?.sort_seq);
        // A row with no stored position sorts after every row that has one.
        if (aSeq !== null && bSeq !== null && aSeq !== bSeq) return aSeq - bSeq;
        if ((aSeq === null) !== (bSeq === null)) return aSeq === null ? 1 : -1;
      }
      return compareByStocktakeItemId(
        parseStocktakeItemSortId(a.item?.stocktake_item_id ?? a.item?.id),
        parseStocktakeItemSortId(b.item?.stocktake_item_id ?? b.item?.id),
        a.index,
        b.index,
      );
    })
    .map(({ item }) => item);
}

export function getSessionAllItems(session) {
  return (
    normalizeStocktakeAllItems(session?.allItems) ||
    normalizeStocktakeAllItems(session?.raw?.all_items) ||
    []
  );
}

/** True when the session carries an explicit all_items list (including empty). */
export function sessionUsesItemList(session) {
  return (
    normalizeStocktakeAllItems(session?.allItems) !== null ||
    normalizeStocktakeAllItems(session?.raw?.all_items) !== null
  );
}

function sessionHasAllItems(session) {
  return sessionUsesItemList(session) || getSessionAllItems(session).length > 0;
}

const stFmtMoney = (n) => {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const stFmtSigned = (n) => (n > 0 ? `+${n}` : `${n}`);

function parseStocktakeUnitCost(...sources) {
  for (const raw of sources) {
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Unit cost for variance $ — prefers API `current_cost_per_item`. */
function resolveStocktakeRowUnitCost(row) {
  return (
    parseStocktakeUnitCost(
      row?.currentCostPerItem,
      row?.current_cost_per_item,
      row?.cost,
      row?.unit_cost,
    ) ?? 0
  );
}

function formatStocktakeVarianceCostFormula(row, varianceUnits) {
  if (varianceUnits === null) return "";
  // Posted/void: formula reflects API discrepancy_cost when present.
  const apiCost = row?.discrepancyCost;
  if (apiCost !== null && apiCost !== undefined && apiCost !== "") {
    const n = Number(apiCost);
    if (Number.isFinite(n)) {
      const signed = varianceUnits < 0 ? -Math.abs(n) : Math.abs(n);
      return stFmtMoney(signed);
    }
  }
  const unitCost = resolveStocktakeRowUnitCost(row);
  const value = varianceUnits * unitCost;
  return `${stFmtSigned(varianceUnits)} × ${stFmtMoney(unitCost)} => ${stFmtMoney(value)}`;
}

function normalizeTotalDataProductsPayload(body) {
  if (!body || typeof body !== "object") return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.result)) return body.result;
  return [];
}

function isTotalDataVariantRow(p) {
  const flag = p.isvarient ?? p.is_variant;
  if (flag !== undefined && flag !== null) {
    return String(flag) === "1";
  }
  return Boolean(p.variant_id);
}

function pickString(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
}

/**
 * Display / payload product label.
 *
 * - "Test 101 / Red/Small/10" → "Test 101 - Red/Small/10"
 *   (only the first product/variant separator becomes " - ";
 *    slashes inside the variant stay as-is)
 * - If product_name has no usable slash form but the row is a variant with
 *   variant / variant_title → `${productTitle} - ${variant}`
 * - Incomplete API labels like "Title - " (trailing separator, no variant)
 *   get the variant appended.
 * - Non-variant names without "/" (e.g. "fifo method merge- fifo method") → as-is
 *
 * Prefer current_product_name from the API; never treat a product-title
 * substring match as "variant already included" (e.g. title "viviyana merge"
 * contains variant "viviyana").
 */
function formatStocktakeProductDisplayName(
  productName,
  fallbackTitle = "Untitled",
  { variantLabel = "", isVariant = false } = {},
) {
  const raw = String(productName ?? "").trim();
  const variant = String(variantLabel ?? "").trim();
  const title = String(fallbackTitle ?? "").trim() || "Untitled";

  const endsWithSeparator = (s) => /(?:\s[-–—]\s*|\s*\/\s*)$/.test(s);
  const stripTrailingSeparator = (s) =>
    String(s ?? "")
      .replace(/(?:\s[-–—]\s*|\s*\/\s*)$/, "")
      .trim();
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  /** True when name ends with " - variant" or " / variant" (not title substring). */
  const hasVariantSuffix = (s, v) => {
    if (!s || !v) return false;
    return new RegExp(
      `(?:\\s[-–—]\\s*|\\s*/\\s*)${escapeRegExp(v)}\\s*$`,
      "i",
    ).test(s);
  };

  if (raw) {
    // Split only on the first " / " between product title and variant label.
    const slash = raw.match(/^(.+?)\s*\/\s*(.+)$/);
    if (slash) {
      const left = slash[1].trim();
      const right = slash[2].trim();
      if (left && right) {
        const variantInName =
          !variant ||
          right.toLowerCase().includes(variant.toLowerCase()) ||
          variant.toLowerCase().includes(right.toLowerCase()) ||
          hasVariantSuffix(raw, variant);
        if (!isVariant || variantInName) {
          return `${left} - ${right}`;
        }
      }
    } else if (!isVariant || !variant) {
      return raw;
    } else if (endsWithSeparator(raw)) {
      // API sometimes returns "Title - " without the variant after the dash.
      const base = stripTrailingSeparator(raw) || title;
      return `${base} - ${variant}`;
    } else if (hasVariantSuffix(raw, variant)) {
      return raw;
    } else {
      // "Title - something" already has a right-hand label — keep as-is.
      const dashParts = raw.match(/^(.+?)\s[-–—]\s+(.+)$/);
      if (dashParts?.[2]?.trim()) return raw;
      // Variant not present as a labeled suffix — append it.
      return `${raw} - ${variant}`;
    }
  }

  // Variant without a usable product_name — build from title + variant.
  if (isVariant && variant) {
    const base = stripTrailingSeparator(
      (raw || title).replace(/\s*\/\s*.*$/, ""),
    ) || title;
    if (hasVariantSuffix(base, variant)) return base;
    return `${base} - ${variant}`;
  }

  return raw || title;
}

/**
 * When items[].current_product_name is blank or ends with a dangling
 * separator ("Title - "), copy the fuller label from the matching all_items
 * entry (same product_id + variant_id). current_product_name remains the
 * display source of truth — this only fills incomplete API values.
 */
function enrichDetailItemsProductNamesFromAllItems(items, allItems) {
  if (!Array.isArray(items) || items.length === 0) return items || [];
  if (!Array.isArray(allItems) || allItems.length === 0) return items;

  const byKey = new Map();
  allItems.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const key = stocktakeRowMatchKey(entry.product_id, entry.variant_id);
    const name = String(entry.current_product_name ?? "").trim();
    if (!key || !name) return;
    if (!byKey.has(key)) byKey.set(key, name);
  });
  if (byKey.size === 0) return items;

  const isIncomplete = (name) =>
    !name || /(?:\s[-–—]\s*|\s*\/\s*)$/.test(name);

  return items.map((item) => {
    const current = String(item?.current_product_name ?? "").trim();
    if (!isIncomplete(current)) return item;
    const better = byKey.get(
      stocktakeRowMatchKey(item?.product_id, item?.variant_id),
    );
    if (!better || better === current) return item;
    return { ...item, current_product_name: better };
  });
}

/** Normalize UPC/SKU/barcode — never show scientific notation (e.g. 7.53068E+11). */
function formatBarcodeSku(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return value.toLocaleString("fullwide", { useGrouping: false });
  }
  const s = String(value).trim();
  if (!s) return "";
  if (/^[\d.]+[eE][+\-]?\d+$/.test(s)) {
    const n = Number(s);
    if (Number.isFinite(n)) {
      return n.toLocaleString("fullwide", { useGrouping: false });
    }
  }
  return s;
}

function pickBarcode(...values) {
  for (const v of values) {
    const formatted = formatBarcodeSku(v);
    if (formatted) return formatted;
  }
  return "";
}

/** Pipe/comma-joined custom codes from product or stocktake detail payloads. */
function pickCustomCode(...values) {
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Local list search for count rows (posted/void filters listed items only).
 * Matches name, UPC, SKU, and custom code (incl. pipe/comma-delimited codes).
 * Note: product_list_v2 matches custom code on the server but does not return
 * the field — callers may stamp the search/scan term onto row.customCode.
 */
function countRowMatchesSearch(row, query) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  if (!row) return false;

  const fields = [
    row.name,
    row.productTitle,
    row.variant,
    row.upc,
    row.sku,
  ];
  for (const field of fields) {
    const s = String(field ?? "").toLowerCase();
    if (s && s.includes(q)) return true;
  }

  const customCode = String(row.customCode ?? "").toLowerCase();
  if (customCode) {
    if (customCode.includes(q)) return true;
    for (const part of customCode.split(/[|,]/)) {
      const code = part.trim();
      if (code && code.includes(q)) return true;
    }
  }
  return false;
}

/**
 * Exact barcode/SKU/UPC/custom-code match for Scan SKU (QV-7161).
 * Do not use includes() — a short UPC must not match a longer one that
 * merely contains it (e.g. 678905 vs 012345678905).
 */
function countRowMatchesExactScanCode(row, code) {
  const codeLower = String(code ?? "")
    .trim()
    .toLowerCase();
  if (!codeLower || !row) return false;

  const sku = String(row.sku || "")
    .trim()
    .toLowerCase();
  const upc = String(row.upc || "")
    .trim()
    .toLowerCase();
  if (sku && sku === codeLower) return true;
  if (upc && upc === codeLower) return true;

  const custom = String(row.customCode ?? "").toLowerCase();
  if (custom) {
    if (custom === codeLower) return true;
    if (
      custom
        .split(/[|,]/)
        .some((part) => part.trim() === codeLower)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Resolve the count row for an exact scan. When a parent/ghost row and a real
 * variant share the same UPC, prefer the variant so scans don't land on the
 * duplicate parent line (variant_id 0).
 */
function findCountRowForExactScanCode(rows, code) {
  const matches = (rows || []).filter((r) =>
    countRowMatchesExactScanCode(r, code),
  );
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  const variantMatch = matches.find((r) => Boolean(r?.isVariant));
  return variantMatch || matches[0];
}

/** Exact barcode match against a catalog pool item (same rules as count rows). */
function catalogProductMatchesExactScanCode(product, code) {
  if (!product) return false;
  return countRowMatchesExactScanCode(
    {
      sku: product.sku,
      upc: product.upc,
      customCode: product.customCode,
    },
    code,
  );
}

/** True when a count row has no counted qty yet (Pending). */
function isStocktakeRowUncounted(row) {
  return (
    row?.counted === null ||
    row?.counted === undefined ||
    row?.counted === ""
  );
}

/**
 * After a counted row leaves "Pending only", pick the nearest remaining
 * pending row — prefer the next one after `fromRowId`, else the previous.
 * `orderIds` (optional) is the row id order to walk (use pre-flush order so
 * moveCountRowToTopOfCounted does not skew "next pending"). QV-7176.
 */
function pickNearestPendingRowIdAfterLeave(rows, fromRowId, orderIds) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const byId = new Map(rows.map((r) => [r.id, r]));
  const order =
    Array.isArray(orderIds) && orderIds.length > 0
      ? orderIds
      : rows.map((r) => r.id);
  const fromIdx = order.indexOf(fromRowId);
  const start = fromIdx < 0 ? 0 : fromIdx + 1;
  for (let i = start; i < order.length; i++) {
    const row = byId.get(order[i]);
    if (row && isStocktakeRowUncounted(row)) return row.id;
  }
  for (let i = (fromIdx < 0 ? order.length : fromIdx) - 1; i >= 0; i--) {
    const row = byId.get(order[i]);
    if (row && isStocktakeRowUncounted(row)) return row.id;
  }
  return null;
}

/**
 * Visible-list index to resume on when `leftRowId` is no longer in
 * `visibleRows` (Pending only filter). Down → first visible after left row
 * in full order; Up → last visible before left row.
 */
function resolveVisibleRowIndexAfterFilterLeave(
  fullRows,
  visibleRows,
  leftRowId,
  direction = 1,
) {
  if (!Array.isArray(visibleRows) || visibleRows.length === 0) return 0;
  const fullIdx = Array.isArray(fullRows)
    ? fullRows.findIndex((r) => r.id === leftRowId)
    : -1;
  if (fullIdx < 0) return direction > 0 ? 0 : visibleRows.length - 1;

  if (direction > 0) {
    for (let i = 0; i < visibleRows.length; i++) {
      const vi = fullRows.findIndex((r) => r.id === visibleRows[i].id);
      if (vi > fullIdx) return i;
    }
    return visibleRows.length - 1;
  }

  for (let i = visibleRows.length - 1; i >= 0; i--) {
    const vi = fullRows.findIndex((r) => r.id === visibleRows[i].id);
    if (vi < fullIdx) return i;
  }
  return 0;
}

/**
 * Append a search/scan term to row.customCode when the API matched it but
 * product_list_v2 does not return custom_code on the payload.
 */
function stampStocktakeRowCustomCode(row, term) {
  const t = String(term ?? "").trim();
  if (!row || !t) return row;
  const existing = String(row.customCode ?? "").trim();
  if (!existing) return { ...row, customCode: t };
  const parts = existing.split(/[|,]/).map((p) => p.trim()).filter(Boolean);
  if (parts.some((p) => p.toLowerCase() === t.toLowerCase())) return row;
  return { ...row, customCode: `${existing}|${t}` };
}

/**
 * Whether an in-session row is one of the products returned by the current
 * catalog search (custom-code hits often omit custom_code on the payload).
 * Matches line key, then UPC/SKU, then product_id (+ variant_id).
 */
function countRowMatchesCatalogSearchHits(row, catalogHits) {
  if (!row || !Array.isArray(catalogHits) || catalogHits.length === 0) {
    return false;
  }

  const rowKey = getCountRowLineKey(row);
  if (
    rowKey &&
    catalogHits.some((p) => getCatalogProductLineKey(p) === rowKey)
  ) {
    return true;
  }

  const rowUpc = String(row.upc || row.sku || "")
    .trim()
    .toLowerCase();
  if (rowUpc) {
    const upcHit = catalogHits.some((p) => {
      const u = String(p.upc || p.sku || "")
        .trim()
        .toLowerCase();
      return u && u === rowUpc;
    });
    if (upcHit) return true;
  }

  const pid = String(row.productId ?? row.catalogProductId ?? "").trim();
  if (!pid) return false;
  return catalogHits.some((p) => {
    if (String(p.productId ?? "").trim() !== pid) return false;
    if (!row.isVariant && !p.isVariant) return true;
    return (
      String(row.variantRowId ?? "").trim() ===
      String(p.variantRowId ?? "").trim()
    );
  });
}

/** Map total_data/products API row → catalog item for stBuildCountRows. */
function mapTotalDataProductRow(p, i) {
  const isVariant = isTotalDataVariantRow(p);
  const title = pickString(p.product_title, p.title) || "Untitled";
  const variantLabel = isVariant ? pickString(p.variant) : "";
  const productNameRaw = pickString(p.product_name, p.product_title, p.title);
  const name = formatStocktakeProductDisplayName(productNameRaw, title, {
    variantLabel,
    isVariant,
  });

  const productId = pickString(
    p.product_id,
    p.variant_product_id,
    !isVariant ? p.id : "",
  );
  const variantRowId = isVariant ? pickString(p.variant_id, p.id) : productId;

  const sku = isVariant
    ? pickBarcode(p.variant_upc, p.variant_squ, p.variant_barcode, p.upc, p.squ)
    : pickBarcode(p.product_upc, p.product_sku, p.product_barcode, p.upc, p.squ);

  const onHand =
    parseFloat(
      isVariant
        ? (p.variant_quantity ?? p.quantity)
        : (p.product_quantity ?? p.quantity),
    ) || 0;

  const retailPrice =
    parseFloat(
      isVariant ? (p.variant_price ?? p.price) : (p.product_price ?? p.price),
    ) || 0;

  const unitCost =
    parseFloat(isVariant ? p.variant_costperItem : p.product_costperItem) || 0;

  const vendorRaw = isVariant
    ? p.variant_assigned_vendors
    : p.product_assigned_vendors;
  const vendor =
    typeof vendorRaw === "string" ? vendorRaw.trim() : pickString(p.vendor);

  const categoryId = String(p.cotegory ?? p.category ?? "");

  return {
    id: variantRowId || productId || String(i),
    productId: productId || variantRowId || String(i),
    variantRowId,
    name,
    sku,
    upc: sku,
    customCode: pickCustomCode(
      isVariant
        ? (p.variant_custom_code ?? p.vcustom_code ?? p.custom_code)
        : (p.product_custom_code ?? p.custom_code),
      p.customCode,
    ),
    variant: variantLabel || null,
    isVariant,
    productTitle: title,
    category: categoryId,
    categoryId,
    vendor,
    brandId: p.brand_id,
    tags: p.tags || "",
    price: { min: retailPrice, max: retailPrice },
    cost: unitCost,
    stock: { onHand, allocated: 0, threshold: 0 },
    color: STOCKTAKE_COLORS[i % STOCKTAKE_COLORS.length],
  };
}

/** Walkaround starts empty; all other scopes show every product from the API pool. */
function resolveSessionItemLimit(session, poolLength) {
  if (session.scope === "walkaround") return 0;
  return poolLength;
}

function trimFilterValue(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

/** Id or array of ids → comma-separated string for API. */
function toCommaSeparatedIds(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter(Boolean)
      .join(",");
  }
  return trimFilterValue(value);
}

/** Resolve scope id from session fields (list row, create payload, or details API). */
export function resolveSessionScopeId(session) {
  if (!session) return "";
  const scopeIds = toCommaSeparatedIds(
    session.scopeFilterIds ?? session.scopeValue ?? session.raw?.scope_id,
  );
  return scopeIds ? scopeIds.split(",")[0] : "";
}

/**
 * Merge get_stocktake_details stocktake header into the open session.
 * Ensures scope_id is available for category/vendor product filters.
 */
export function enrichSessionFromStocktakeDetails(session, stocktake) {
  if (!session || !stocktake) return session;

  const scopeId = trimFilterValue(stocktake.scope_id);
  const scope = stocktake.scope_type
    ? mapApiScopeTypeToUi(stocktake.scope_type)
    : session.scope;

  return {
    ...session,
    scope,
    ...(scopeId ? { scopeFilterIds: scopeId } : {}),
    ...(stocktake.scope_name?.trim()
      ? { scopeLabel: stocktake.scope_name.trim() }
      : {}),
    status:
      stocktake.status != null
        ? normalizeStocktakeStatus(stocktake)
        : session.status,
    statusLabel: stocktake.status_label?.trim() || session.statusLabel,
    allItems:
      normalizeStocktakeAllItems(stocktake.all_items) || session.allItems,
    raw: { ...(session.raw || {}), ...stocktake },
  };
}

/**
 * POST body for total_data/products.
 * Required: merchant_id. Optional: categories, brands, tags, vendors,
 * category_id, vendor_id, search (omitted when empty — do not send falsy values).
 */
function buildTotalDataProductsRequestBody(merchantId, filters = {}) {
  // Virtual kits never hold stock, so they are not counted (HQ parity).
  const body = { merchant_id: String(merchantId).trim(), kit_scope: "stocked" };

  const categories = toCommaSeparatedIds(filters.categories);
  const brands = toCommaSeparatedIds(filters.brands);
  const tags = toCommaSeparatedIds(filters.tags);
  const vendors = toCommaSeparatedIds(filters.vendors);
  const search = trimFilterValue(filters.search);

  const categoryId = trimFilterValue(filters.category_id);
  const vendorId = trimFilterValue(filters.vendor_id);

  if (categories) body.categories = categories;
  if (brands) body.brands = brands;
  if (tags) body.tags = tags;
  if (vendors) body.vendors = vendors;
  if (categoryId) body.category_id = categoryId;
  if (vendorId) body.vendor_id = vendorId;
  if (search) body.search = search;

  return body;
}

/**
 * Map stocktake session → API filter fields.
 * Use scopeFilterIds (or scopeValue) as comma-separated id(s) for scoped counts.
 */
function getSessionProductFilters(session) {
  if (!session) return {};

  const scoped = getSessionCatalogSearchFilters(session);
  if (scoped.category_id || scoped.vendor_id) return scoped;

  const filters = {};

  if (session.categories)
    filters.categories = toCommaSeparatedIds(session.categories);
  if (session.brands) filters.brands = toCommaSeparatedIds(session.brands);
  if (session.tags) filters.tags = toCommaSeparatedIds(session.tags);
  if (session.vendors) filters.vendors = toCommaSeparatedIds(session.vendors);

  const scopeIds = toCommaSeparatedIds(
    session.scopeFilterIds ?? session.scopeValue ?? session.raw?.scope_id,
  );
  if (scopeIds) {
    switch (session.scope) {
      case "category":
        filters.categories = filters.categories || scopeIds;
        break;
      case "vendor":
        filters.vendors = filters.vendors || scopeIds;
        break;
      default:
        break;
    }
  }

  return filters;
}

/**
 * Catalog search filters for category/vendor sessions (total_data/products).
 * Sends category_id or vendor_id so the API returns scoped variants.
 */
function getSessionCatalogSearchFilters(session) {
  if (!session) return {};

  const scopeId = resolveSessionScopeId(session);
  if (!scopeId) return {};

  switch (session.scope) {
    case "category":
      return { category_id: scopeId };
    case "vendor":
      return { vendor_id: scopeId };
    default:
      return {};
  }
}

/**
 * Fetch products from total_data/products.
 * Response shape: { status, msg, count, data: [...] }
 */
async function getProductsPool(merchantId, filters = {}, auth = {}) {
  if (!merchantId || !STOCKTAKE_TOTAL_PRODUCTS_URL) return [];
  try {
    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_TOTAL_PRODUCTS_URL,
      withStocktakeAuth(
        buildTotalDataProductsRequestBody(merchantId, filters),
        auth,
      ),
    );
    const list = normalizeTotalDataProductsPayload(response.data);
    return list.map(mapTotalDataProductRow);
  } catch (error) {
    console.error("Error getting products pool:", error);
    // null signals a failed fetch (vs an empty catalog) so the caller can toast.
    return null;
  }
}

/** Products per page for category/vendor scoped pools (product_list_v2). */
const STOCKTAKE_SCOPED_POOL_PER_PAGE = 50;

/**
 * Map a new_purchase_orders_product_list_v2 product/variant pair → the same
 * catalog item shape as mapTotalDataProductRow, so stBuildCountRows and the
 * scan/search helpers keep working unchanged.
 */
function mapPoProductRowToCatalogItem(product, variant, index) {
  const isVariant = Boolean(variant);
  const source = variant || product;

  const title =
    pickString(product?.product_title) || "Untitled";
  const variantLabel = isVariant
    ? pickString(variant.variant_name, variant.variant_title, variant.variant)
    : "";
  const productNameRaw = pickString(
    isVariant ? variant?.product_name : "",
    product?.product_name,
    product?.product_title,
  );
  const name = formatStocktakeProductDisplayName(productNameRaw, title, {
    variantLabel,
    isVariant,
  });

  const productId = pickString(product?.product_id);
  const variantRowId = isVariant
    ? pickString(variant.varient_id, variant.variant_id)
    : productId;

  const sku = pickBarcode(source?.upc, product?.upc);
  const onHand = parseFloat(source?.quantity) || 0;
  const unitCost = parseFloat(source?.cost_price) || 0;
  const vendor = pickString(
    source?.vendor_name,
    product?.vendor_name,
    product?.company_name,
  );

  return {
    id: variantRowId || productId || String(index),
    productId: productId || variantRowId || String(index),
    variantRowId,
    name,
    sku,
    upc: sku,
    customCode: pickCustomCode(
      source?.custom_code,
      source?.customCode,
      source?.vcustom_code,
      variant?.custom_code,
      product?.custom_code,
      product?.customCode,
    ),
    variant: variantLabel || null,
    isVariant,
    productTitle: title,
    category: "",
    categoryId: "",
    vendor,
    brandId: "",
    tags: "",
    price: { min: unitCost, max: unitCost },
    cost: unitCost,
    stock: { onHand, allocated: 0, threshold: 0 },
    color: STOCKTAKE_COLORS[index % STOCKTAKE_COLORS.length],
  };
}

/** Flatten one v2 product (with nested variants[]) into catalog items. */
function flattenPoProductToCatalogItems(product, startIndex) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (variants.length === 0) {
    return [mapPoProductRowToCatalogItem(product, null, startIndex)];
  }
  return variants.map((variant, i) =>
    mapPoProductRowToCatalogItem(product, variant, startIndex + i),
  );
}

/**
 * Fetch ONE page of the scoped product pool from
 * new_purchase_orders_product_list_v2 (category/vendor scoped stocktakes).
 * Returns { items, hasMore, total } — items use the catalog item shape.
 */
async function fetchScopedProductsPoolPage({
  merchantId,
  scope,
  scopeId,
  page = 1,
  perPage = STOCKTAKE_SCOPED_POOL_PER_PAGE,
  search = "",
  auth = {},
  token,
}) {
  const body = withStocktakeAuth(
    {
      merchant_id: String(merchantId).trim(),
      vendor_id: "",
      filter_vendor_id: scope === "vendor" ? String(scopeId || "") : "",
      category_id: scope === "category" ? String(scopeId || "") : "",
      search_by_product: String(search || "").trim(),
      page: Number(page) || 1,
      per_page: perPage,
      // Virtual kits never hold stock, so they are not counted (HQ parity).
      kit_scope: "stocked",
    },
    auth,
  );

  // Same host switch as the PO "Add items" modal — Node API, gated by
  // FORCE_NODE_API_FOR_STOCKTAKE_AND_PURCHASE_ORDER in Constants/Config.js.
  const response = await axios.post(
    resolveProductListBaseUrl() + NEW_PURCHASE_ORDER_PRODUCT_LIST,
    body,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );

  const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
  const startIndex = (Number(page) - 1) * perPage;
  const items = rows.flatMap((product, i) =>
    flattenPoProductToCatalogItems(product, startIndex + i),
  );

  const pagination = response?.data?.pagination;
  const resolvedPage = Number(pagination?.page) || Number(page) || 1;
  const resolvedPerPage = Number(pagination?.per_page) || perPage;
  const total = Number(pagination?.total);
  const hasMore = Number.isFinite(total)
    ? resolvedPage * resolvedPerPage < total
    : rows.length >= resolvedPerPage;

  return { items, hasMore, total: Number.isFinite(total) ? total : undefined };
}

/**
 * Fast exact-match scan lookup (new_purchase_order_product_by_upc).
 * The backend matches variant UPC → product UPC/SKU → custom code, same as
 * the PO "Add items" modal. Pass categoryId/vendorId to restrict the match
 * to a scoped stocktake's category or vendor (sent as cat_id / ven_id).
 * Returns { item, message } — item is a catalog item or null; message
 * carries the API's not-found text (e.g. "No product found for this UPC or
 * code") so callers can surface it.
 */
async function fetchStocktakeProductByScan({
  merchantId,
  code,
  categoryId = "",
  vendorId = "",
  auth = {},
  token,
}) {
  const trimmed = String(code || "").trim();
  if (!merchantId || !trimmed) return { item: null, message: "" };

  try {
    const response = await axios.post(
      BASE_NODE_URL + NEW_PURCHASE_ORDER_PRODUCT_BY_UPC,
      withStocktakeAuth(
        {
          merchant_id: String(merchantId).trim(),
          upc: trimmed,
          cat_id: String(categoryId || "").trim(),
          ven_id: String(vendorId || "").trim(),
          kit_scope: "stocked",
        },
        auth,
      ),
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    const message = pickString(response?.data?.message);
    const row = response?.data?.status ? response.data.data : null;
    if (!row) return { item: null, message };

    // Flat row: variant fields live on the same object, so pass it as both
    // product and variant for the shared mapper.
    const isVariant =
      String(row.isvarient ?? "") === "1" ||
      Boolean(String(row.varient_id ?? "").trim());
    return {
      item: mapPoProductRowToCatalogItem(row, isVariant ? row : null, 0),
      message,
    };
  } catch (error) {
    console.error("Error scanning product by UPC:", error);
    return { item: null, message: "" };
  }
}

/** Build table rows for a session from mapped catalog pool. */
const stBuildCountRows = (session, productPool) => {
  if (session.scope === "walkaround") return [];
  if (!productPool?.length) return [];

  const itemLimit = resolveSessionItemLimit(session, productPool.length);
  if (itemLimit === 0) return [];

  let products = productPool;

  const apiScoped =
    (session.scope === "category" || session.scope === "vendor") &&
    Boolean(resolveSessionScopeId(session));

  if (!apiScoped && session.scope === "category" && session.scopeLabel) {
    const label = String(session.scopeLabel).toLowerCase();
    products = productPool.filter(
      (p) =>
        String(p.categoryId).toLowerCase() === label ||
        String(p.category).toLowerCase() === label,
    );
  } else if (!apiScoped && session.scope === "vendor" && session.scopeLabel) {
    const label = session.scopeLabel.toLowerCase();
    products = productPool.filter(
      (p) => p.vendor && p.vendor.toLowerCase() === label,
    );
  }

  if (
    products.length === 0 &&
    productPool.length > 0 &&
    !sessionHasAllItems(session)
  ) {
    products = productPool;
  }

  return products.map((p) => {
    const expected = p.stock?.onHand ?? 0;
    const isVariant = Boolean(p.isVariant);
    const variantRowId = isVariant
      ? String(p.variantRowId ?? p.id ?? "").trim()
      : String(p.productId ?? p.id ?? "").trim();
    return {
      id: isVariant ? variantRowId : String(p.productId ?? p.id ?? "").trim(),
      productId: p.productId,
      catalogProductId: p.productId,
      variantRowId,
      isVariant,
      variant: p.variant || "",
      productTitle: p.productTitle || p.name,
      upc: formatBarcodeSku(p.upc || p.sku),
      sku: formatBarcodeSku(p.sku),
      name: p.name,
      category: p.category,
      categoryId: p.categoryId ?? p.category,
      vendor: p.vendor,
      color: p.color,
      expected,
      counted: null,
      cost: p.cost ?? (p.price?.min || 0) * 0.6,
      currentCostPerItem: p.cost ?? (p.price?.min || 0) * 0.6,
      threshold: p.stock?.threshold ?? 0,
      stocktakeItemId: null,
      flag: null,
      note: "",
    };
  });
};

/** Map UI count row → save_counts / post_adjustment API item. */
function mapRowToSaveCountItem(row) {
  const hasCounted =
    row.counted !== null &&
    row.counted !== undefined &&
    row.counted !== "";
  const varianceUnits = hasCounted ? stVariance(row) : null;
  const varianceCost = hasCounted ? stVarianceValue(row) : null;

  const title = row.productTitle || "";
  const variantLabel = row.variant || "";
  // Always send the display label (e.g. "Test 101 - Red/Small/10").
  const product_name =
    row.name ||
    formatStocktakeProductDisplayName(title, title, {
      variantLabel,
      isVariant: Boolean(row.isVariant),
    }) ||
    "";

  const item = {
    product_id: row.productId,
    variant_id: row.isVariant ? row.variantRowId : "",
    upc: formatBarcodeSku(row.upc || row.sku),
    category_id: String(row.categoryId ?? row.category ?? ""),
    product_name,
    variant: variantLabel,
    expected_qty: row.expected ?? 0,
    unit_cost: resolveStocktakeRowUnitCost(row),
    // Blank (not-yet-counted) rows send "" — e.g. a product just added from
    // search/scan is registered without a counted quantity.
    counted_qty: row.counted ?? "",
    discrepancy: hasCounted ? (varianceUnits ?? 0) : "",
    discrepancy_cost: hasCounted ? (varianceCost ?? 0) : "0",
    reason_id:
      row.reasonId != null && row.reasonId !== ""
        ? String(row.reasonId)
        : "",
    note: row.note ?? "",
  };
  if (row.stocktakeItemId) {
    item.stocktake_item_id = row.stocktakeItemId;
  }
  // Screen position, stamped by assignStocktakeSortSeq just before the payload
  // is built. Persisted as stocktake_items.sort_seq and read back on open.
  const sortSeq = parseStocktakeSortSeq(row.sortSeq);
  if (sortSeq !== null) {
    item.sort_seq = sortSeq;
  }
  return item;
}

/** Map only rows with a saved count → save_counts / post_adjustment item shape. */
function mapCountedRowsToSaveCountItems(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(
      (row) =>
        row.counted !== null &&
        row.counted !== undefined &&
        row.counted !== "",
    )
    .map(mapRowToSaveCountItem);
}

/**
 * Walkaround / Ad-hoc Save as Draft — lean payload:
 * - counted rows (including newly added that were counted)
 * - newly added search/scan rows not yet saved (may register with blank count)
 * - drafted rows whose count was cleared this session (`clearedCountPendingSave`)
 *   Ad-hoc has no delete from `items`; clear must send blank counted_qty.
 * Never-counted pending lines already in items/all_items are omitted.
 */
function mapLeanDraftRowsToSaveCountItems(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => {
      if (!row?.productId || String(row.productId).trim() === "") return false;
      const hasCounted =
        row.counted !== null &&
        row.counted !== undefined &&
        row.counted !== "";
      if (hasCounted) return true;
      // Count cleared after draft — must update backend (esp. adhoc, no delete).
      if (row.clearedCountPendingSave) return true;
      const isSaved =
        row.stocktakeItemId != null &&
        String(row.stocktakeItemId).trim() !== "";
      // Local search/scan add not yet drafted — allow blank draft register.
      return Boolean(row.addedFromSearchOrScan) && !isSaved;
    })
    .map(mapRowToSaveCountItem);
}

/** @deprecated Use mapLeanDraftRowsToSaveCountItems */
function mapWalkaroundDraftRowsToSaveCountItems(rows) {
  return mapLeanDraftRowsToSaveCountItems(rows);
}

/** Map all session rows → save_counts items (blank counted_qty allowed for Draft). */
function mapRowsToSaveCountItems(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row?.productId != null && String(row.productId).trim() !== "")
    .map(mapRowToSaveCountItem);
}

/**
 * Persist one or more counted items for a stocktake session.
 * POST StocktakeNewUI/save_counts
 */
async function saveStocktakeCounts({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  employeeId,
  scopeType,
  items,
}) {
  if (!merchantId || !token || !stocktakeId || !items?.length) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });
    formData.append("stocktake_id", stocktakeId ?? "");
    formData.append("employee_id", String(employeeId ?? "0").trim());
    formData.append("scope_type", String(scopeType ?? "").trim());
    // Send items as a JSON string so the nested array is preserved in multipart/form-data
    formData.append("items", JSON.stringify(items));

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_SAVE_COUNTS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("saveStocktakeCounts", error);
    throw error;
  }
}

/**
 * Fetch a single stocktake (header + saved counted items) for editing.
 * POST StocktakeNewUI/get_stocktake_details
 * Response: { status, message, result: { stocktake, items: [...] } }
 */
async function getStocktakeDetails({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
}) {
  if (!merchantId || !token || !stocktakeId) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });
    formData.append("stocktake_id", stocktakeId ?? "");

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_GET_DETAILS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("getStocktakeDetails", error);
    throw error;
  }
}

/**
 * Parse stocktake.all_items ([{ "<product_id>": "<variant_id_csv>" }, ...])
 * into lookup sets used to filter the catalog pool to this stocktake's items.
 */
function buildStocktakeItemFilter(allItems) {
  const productIds = new Set();
  const variantIds = new Set();
  const productHasVariants = new Set();

  (Array.isArray(allItems) ? allItems : []).forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const productId = Object.keys(entry)[0];
    if (!productId) return;
    productIds.add(String(productId).trim());

    const csv = entry[productId];
    if (csv != null && String(csv).trim() !== "") {
      productHasVariants.add(String(productId).trim());
      String(csv)
        .split(",")
        .forEach((v) => {
          const variantId = v.trim();
          if (variantId) variantIds.add(variantId);
        });
    }
  });

  return { productIds, variantIds, productHasVariants };
}

/**
 * Keep only the catalog products that belong to the current stocktake.
 * - product must be listed in all_items
 * - for products that list specific variants, keep only those variant rows
 */
function filterPoolByStocktakeItems(pool, filter) {
  if (!filter || filter.productIds.size === 0) return [];
  return (pool || []).filter((p) => {
    const productId = String(p.productId ?? "").trim();
    if (!filter.productIds.has(productId)) return false;

    if (filter.productHasVariants.has(productId)) {
      if (!p.isVariant) return false;
      const variantId = String(p.variantRowId ?? p.id ?? "").trim();
      return filter.variantIds.has(variantId);
    }

    return !p.isVariant;
  });
}

/**
 * Net totals for post_stocktake_adjustment — sum of Units and Δ $ columns.
 */
function computeStocktakeDiscrepancyTotals(summary) {
  const totalDiscrepancy =
    (summary?.gainUnits ?? 0) - (summary?.lossUnits ?? 0);
  const totalDiscrepancyCost =
    (summary?.gainValue ?? 0) + (summary?.lossValue ?? 0);
  return { totalDiscrepancy, totalDiscrepancyCost };
}

/**
 * Post counted quantities as inventory adjustments for a stocktake session.
 * POST StocktakeNewUI/post_stocktake_adjustment (multipart/form-data)
 */
async function postStocktakeAdjustment({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  employeeId,
  totalDiscrepancy,
  totalDiscrepancyCost,
  totalItems,
  scopeType,
  items,
}) {
  if (!merchantId || !token || !stocktakeId) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    formData.append("stocktake_id", String(stocktakeId).trim());
    formData.append("employee_id", String(employeeId ?? "0").trim());
    formData.append("total_discrepancy", String(totalDiscrepancy ?? 0));
    formData.append(
      "total_discrepancy_cost",
      Number(totalDiscrepancyCost ?? 0).toFixed(2),
    );
    formData.append(
      "total_items",
      String(
        Number.isFinite(Number(totalItems))
          ? Math.max(0, Math.trunc(Number(totalItems)))
          : Array.isArray(items)
            ? items.length
            : 0,
      ),
    );
    formData.append("scope_type", String(scopeType ?? "").trim());
    formData.append("items", JSON.stringify(Array.isArray(items) ? items : []));
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_POST_ADJUSTMENT,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("postStocktakeAdjustment", error);
    throw error;
  }
}

/**
 * Void a stocktake session without posting inventory adjustments.
 * POST StocktakeNewUI/void_stocktake (multipart/form-data)
 * QV-6358: merchant_id, stocktake_id, employee_id
 */
async function voidStocktake({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  employeeId,
}) {
  if (!merchantId || !token || !stocktakeId) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    formData.append("stocktake_id", String(stocktakeId).trim());
    formData.append("employee_id", String(employeeId ?? "0").trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_VOID_STOCKTAKE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("voidStocktake", error);
    throw error;
  }
}

/**
 * Delete a stocktake from the list.
 * POST StocktakeNewUI/delete_stocktake (multipart/form-data)
 * Input: merchant_id, stocktake_id (+ auth fields).
 */
async function deleteStocktake({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
}) {
  if (!merchantId || !token || !stocktakeId) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    formData.append("stocktake_id", String(stocktakeId).trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });

    const response = await axios.post(BASE_NODE_URL + STOCKTAKE_DELETE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("deleteStocktake", error);
    throw error;
  }
}

/**
 * Email stocktake report to a recipient.
 * POST Stocktake_react_api/email_stocktake (multipart/form-data)
 */
async function emailStocktake({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  email,
  employeeId,
}) {
  if (!merchantId || !token || !stocktakeId || !email) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("email", String(email).trim());
    formData.append("stocktake_id", String(stocktakeId).trim());
    formData.append("merchant_id", String(merchantId).trim());
    formData.append("employee_id", String(employeeId ?? "0").trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });

    const response = await axios.post(BASE_URL + STOCKTAKE_EMAIL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });
    return { httpStatus: response.status, data: response.data };
  } catch (error) {
    console.error("emailStocktake", error);
    throw error;
  }
}

/**
 * Toggle the "recount" flag on a saved stocktake item.
 * POST StocktakeNewUI/update_recount_flag (multipart/form-data)
 */
async function updateStocktakeRecountFlag({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  stocktakeItemId,
  recountFlag,
}) {
  if (!merchantId || !token || !stocktakeId || !stocktakeItemId) {
    return { status: false };
  }
  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });
    formData.append("stocktake_id", stocktakeId ?? "");
    formData.append("stocktake_item_id", stocktakeItemId ?? "");
    formData.append("recount_flag", recountFlag ?? "0");

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_UPDATE_RECOUNT_FLAG,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("updateStocktakeRecountFlag", error);
    throw error;
  }
}

/**
 * Canonical stocktake line identity: variants compare by variant_id only;
 * non-variants compare by product_id.
 */
function getStocktakeLineKey(isVariant, productId, variantRowId) {
  if (isVariant) {
    const vid = String(variantRowId ?? "").trim();
    return vid ? `v:${vid}` : "";
  }
  const pid = String(productId ?? "").trim();
  return pid ? `p:${pid}` : "";
}

/** Normalized key for saved API items ↔ count rows (variant_id when variant). */
function stocktakeRowMatchKey(productId, variantId) {
  const vid = String(variantId ?? "").trim();
  const isVariant = Boolean(vid && vid !== "0");
  return getStocktakeLineKey(
    isVariant,
    productId,
    isVariant ? vid : productId,
  );
}

/** Unique key for a catalog search line (variant rows keyed by variant_id). */
function getCatalogProductLineKey(product) {
  if (!product) return "";
  return getStocktakeLineKey(
    Boolean(product.isVariant),
    product.productId ?? product.id,
    product.variantRowId ?? product.id,
  );
}

/** Unique key for an in-session count row (variant rows keyed by variant_id). */
function getCountRowLineKey(row) {
  if (!row) return "";
  return getStocktakeLineKey(
    Boolean(row.isVariant),
    row.productId ?? row.catalogProductId ?? row.id,
    row.variantRowId ?? row.id,
  );
}

function countRowMatchesCatalogProduct(row, product) {
  if (!row || !product) return false;
  return getCountRowLineKey(row) === getCatalogProductLineKey(product);
}

function findCountRowForCatalogProduct(rows, product) {
  if (!Array.isArray(rows) || !product) return undefined;
  // Line key first; UPC / product_id fallback when ids diverge across payloads.
  return rows.find((r) => countRowMatchesCatalogSearchHits(r, [product]));
}

/** Move a freshly counted row to the top of the list (latest count first). */
function moveCountRowToTopOfCounted(rows, rowId) {
  if (!Array.isArray(rows) || !rowId) return rows;
  const idx = rows.findIndex((r) => r.id === rowId);
  if (idx < 0) return rows;

  const row = rows[idx];
  if (row.counted === null || row.counted === undefined) return rows;

  const next = [...rows];
  next.splice(idx, 1);
  next.unshift(row);
  return next;
}

/**
 * Apply moveCountRowToTopOfCounted for each id. Ids are passed in count order,
 * so the last one counted ends up on top.
 */
function moveCountRowsToTopOfCounted(rows, rowIds) {
  if (!Array.isArray(rows) || !Array.isArray(rowIds) || rowIds.length === 0) {
    return rows;
  }
  return rowIds.reduce(
    (acc, rowId) => moveCountRowToTopOfCounted(acc, rowId),
    rows,
  );
}

/**
 * The one place a save payload is built — Save as Draft and Post Adjustment
 * both go through here.
 *
 * Stamps every row with its on-screen position, then picks the rows this scope
 * sends. The returned array is in screen order and each item carries the same
 * order explicitly as sort_seq, so a payload that is only a subset of the list
 * (category/vendor send counted rows only) still describes the real positions.
 *
 * Callers that keep the session open must commit `stampedRows` back to state,
 * or the next save re-sends the positions from before this one.
 */
function buildStocktakeSavePayload(rows, { scope, countedOnly = false } = {}) {
  const stampedRows = assignStocktakeSortSeq(rows);
  const items =
    countedOnly || scope === "category" || scope === "vendor"
      ? mapCountedRowsToSaveCountItems(stampedRows)
      : scope === "walkaround" || scope === "adhoc"
        ? mapLeanDraftRowsToSaveCountItems(stampedRows)
        : mapRowsToSaveCountItems(stampedRows);
  return { stampedRows, items };
}

/**
 * Count drafts are stored in a plain object keyed by row id. Those ids are
 * numeric strings, so Object.keys() returns them in ascending id order and
 * the order the user actually counted rows in is lost. `preferredOrder` is
 * the first-touch order tracked by the layout; untracked keys are appended.
 */
function orderCountDraftRowIds(countDrafts, preferredOrder) {
  if (!countDrafts || typeof countDrafts !== "object") return [];
  const hasDraft = (id) =>
    Object.prototype.hasOwnProperty.call(countDrafts, id);

  const ordered = [];
  const seen = new Set();
  if (Array.isArray(preferredOrder)) {
    for (const id of preferredOrder) {
      if (!seen.has(id) && hasDraft(id)) {
        ordered.push(id);
        seen.add(id);
      }
    }
  }
  for (const id of Object.keys(countDrafts)) {
    if (!seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }
  return ordered;
}

function countRowsMatchSameLine(a, b) {
  if (!a || !b) return false;
  return getCountRowLineKey(a) === getCountRowLineKey(b);
}

/** Build an ad-hoc count row from a mapped catalog product line. */
function buildAdHocCountRowFromCatalog(product, initialCount = 1) {
  const isVariant = Boolean(product.isVariant);
  const productId = String(product.productId ?? product.id ?? "").trim();
  const variantRowId = isVariant
    ? String(product.variantRowId ?? product.id ?? "").trim()
    : productId;
  const rowId = isVariant ? variantRowId : productId;

  return {
    id: rowId,
    productId,
    catalogProductId: productId,
    variantRowId,
    isVariant,
    variant: product.variant || "",
    productTitle: product.productTitle || product.name,
    upc: formatBarcodeSku(product.upc || product.sku),
    sku: formatBarcodeSku(product.sku),
    customCode: pickCustomCode(product.customCode, product.custom_code),
    name: product.name,
    category: product.category,
    categoryId: product.categoryId ?? product.category,
    vendor: product.vendor,
    color: product.color,
    expected: product.stock?.onHand ?? 0,
    counted: initialCount,
    cost: product.cost ?? (product.price?.min || 0) * 0.6,
    currentCostPerItem:
      product.currentCostPerItem ??
      product.cost ??
      (product.price?.min || 0) * 0.6,
    threshold: product.stock?.threshold ?? 0,
    stocktakeItemId: null,
    flag: null,
    note: "",
    addedAdHoc: true,
    // Added via search / Scan SKU this session — removable while still local.
    addedFromSearchOrScan: true,
  };
}

/**
 * Local-only search/scan adds (never save_counts'd in this session) can be
 * dropped from the list with ×. Once Draft/remove has touched the row, it is
 * session-owned — hide ×; trash only while a count is present.
 */
function canRemoveLocalStocktakeRow(row) {
  if (!row?.addedFromSearchOrScan) return false;
  if (row.stocktakeItemId) return false;
  return true;
}

/** Show remove control: local search/scan drop (×), or clear a counted row (trash). */
function canShowStocktakeRowRemove(row) {
  if (canRemoveLocalStocktakeRow(row)) return true;
  return row?.counted !== null && row?.counted !== undefined && row?.counted !== "";
}

/**
 * True when an all_items entry is the enriched object shape
 * ({ product_id, variant_id, upc, product_title, … }) rather than the legacy
 * { "<productId>": "<variantCsv>" } map.
 */
function isRichAllItemsEntry(entry) {
  return Boolean(
    entry && typeof entry === "object" && entry.product_id != null,
  );
}

function isGhostParentVariantId(variantId) {
  const vid = String(variantId ?? "").trim();
  return !vid || vid === "0";
}

/**
 * Drop ghost parent all_items rows (variant_id 0/empty) when the same
 * product_id already has a real variant entry. Backend sometimes returns both
 * a parent and a variant that share a UPC, which duplicates the count table.
 */
function dedupeRichStocktakeAllItems(entries) {
  const list = (entries || []).filter(isRichAllItemsEntry);
  if (list.length === 0) return list;

  const productsWithRealVariants = new Set(
    list
      .filter((e) => !isGhostParentVariantId(e.variant_id))
      .map((e) => String(e.product_id)),
  );

  return list.filter((e) => {
    if (!isGhostParentVariantId(e.variant_id)) return true;
    return !productsWithRealVariants.has(String(e.product_id));
  });
}

/**
 * Remap saved detail items that still point at a ghost parent (variant_id 0)
 * onto the real variant that shares the same product_id + UPC in all_items.
 * Prevents counted ghost lines from reappearing as extra rows after dedupe.
 */
function remapGhostParentDetailItems(items, richEntries) {
  if (!Array.isArray(items) || items.length === 0) return items || [];

  const variantByProductUpc = new Map();
  (richEntries || []).filter(isRichAllItemsEntry).forEach((e) => {
    if (isGhostParentVariantId(e.variant_id)) return;
    const upc = formatBarcodeSku(e.upc);
    if (!upc) return;
    const key = `${String(e.product_id)}|${String(upc).toLowerCase()}`;
    if (!variantByProductUpc.has(key)) {
      variantByProductUpc.set(key, String(e.variant_id));
    }
  });
  if (variantByProductUpc.size === 0) return items;

  return items.map((it) => {
    if (!isGhostParentVariantId(it?.variant_id)) return it;
    const upc = formatBarcodeSku(it.upc);
    if (!upc) return it;
    const key = `${String(it.product_id)}|${String(upc).toLowerCase()}`;
    const realVariantId = variantByProductUpc.get(key);
    if (!realVariantId) return it;
    return { ...it, variant_id: realVariantId };
  });
}

/**
 * Build a count row from an enriched get_stocktake_details all_items entry.
 * Used for ad-hoc counts so the full products pool API never has to load —
 * all_items already carries the product/variant data.
 */
function buildRowFromAllItemsEntry(entry, i = 0) {
  const isVariant = Boolean(
    entry.variant_id && String(entry.variant_id) !== "0",
  );
  const productId = String(entry.product_id);
  const variantRowId = isVariant ? String(entry.variant_id) : productId;
  const title = pickString(entry.product_title) || "Untitled";
  const variantLabel = isVariant
    ? pickString(entry.variant_title, entry.variant)
    : "";
  const name = formatStocktakeProductDisplayName(
    pickString(
      entry.current_product_name,
      entry.product_name,
      entry.product_title,
    ),
    title,
    { variantLabel, isVariant },
  );
  const expectedRaw =
    entry.current_item_quantity ??
    entry.current_qty ??
    entry.qty ??
    entry.quantity ??
    null;
  const expected =
    expectedRaw != null && expectedRaw !== "" ? Number(expectedRaw) : 0;
  const unitCost =
    parseStocktakeUnitCost(
      entry.current_cost_per_item,
      entry.unit_cost,
      entry.cost,
      entry.price,
    ) ?? 0;

  return {
    id: variantRowId,
    productId,
    catalogProductId: productId,
    variantRowId,
    isVariant,
    variant: variantLabel,
    productTitle: title,
    upc: formatBarcodeSku(entry.upc),
    sku: formatBarcodeSku(entry.sku || entry.product_sku || entry.upc),
    customCode: pickCustomCode(
      entry.custom_code,
      entry.customCode,
      entry.vcustom_code,
    ),
    name,
    category: String(entry.category_id ?? ""),
    categoryId: String(entry.category_id ?? ""),
    vendor: "",
    color: STOCKTAKE_COLORS[i % STOCKTAKE_COLORS.length],
    expected: Number.isNaN(expected) ? 0 : expected,
    counted: null,
    cost: unitCost,
    currentCostPerItem: unitCost,
    threshold: 0,
    stocktakeItemId: null,
    flag: null,
    note: "",
    // From session all_items / pool — not a search/scan add this session.
    addedAdHoc: false,
    addedFromSearchOrScan: false,
  };
}

/**
 * Discrepancy reasons. The Reason column is intentionally NOT rendered in the
 * Count entries table (that stays as it is on live) — these are only used to
 * label reasons on the printed report and in the CSV exports.
 */
const ST_REASONS = [
  { id: 1, label: "Damage" },
  { id: 2, label: "Theft / shrink" },
  { id: 3, label: "Mislocated" },
  { id: 4, label: "Receiving error" },
  { id: 5, label: "Already sold (not POS)" },
  { id: 6, label: "Counting error" },
  { id: 7, label: "Other" },
];

function getStocktakeReasonLabel(reasonId) {
  if (reasonId == null || reasonId === "") return "";
  const id = Number(String(reasonId).trim());
  if (!Number.isFinite(id) || id <= 0) return "";
  return ST_REASONS.find((r) => r.id === id)?.label ?? "";
}

/** Prefer stored label, else map reasonId / reason_id to ST_REASONS label. */
function resolveStocktakeReasonLabel(rowOrReason) {
  if (rowOrReason == null) return "";
  if (typeof rowOrReason === "string" || typeof rowOrReason === "number") {
    const raw = String(rowOrReason).trim();
    if (!raw) return "";
    // Numeric string → treat as reason id; otherwise return as-is (already a label).
    if (/^\d+$/.test(raw)) return getStocktakeReasonLabel(raw) || "";
    return raw;
  }
  const existing =
    typeof rowOrReason.reason === "string" ? rowOrReason.reason.trim() : "";
  if (existing && !/^\d+$/.test(existing)) return existing;
  if (existing && /^\d+$/.test(existing)) {
    return getStocktakeReasonLabel(existing) || existing;
  }
  return getStocktakeReasonLabel(
    rowOrReason.reasonId ??
      rowOrReason.reason_id ??
      rowOrReason.ReasonId ??
      rowOrReason.Reason,
  );
}

/**
 * Map a saved get_stocktake_details item → patch for an existing count row.
 * Until the stocktake is posted, variance/gain/loss must be computed against
 * the live product quantity (current_item_quantity); once posted the frozen
 * expected_qty is used instead.
 */
function mapDetailItemToRowPatch(item, isPosted = false) {
  const sortSeq = parseStocktakeSortSeq(item.sort_seq);
  const counted =
    item.counted_qty != null && item.counted_qty !== ""
      ? Number(item.counted_qty)
      : item.new_qty != null && item.new_qty !== ""
        ? Number(item.new_qty)
        : null;
  const liveQty =
    !isPosted &&
      item.current_item_quantity != null &&
      item.current_item_quantity !== ""
      ? Number(item.current_item_quantity)
      : null;
  const expected =
    liveQty != null && !Number.isNaN(liveQty)
      ? liveQty
      : item.expected_qty != null && item.expected_qty !== ""
        ? Number(item.expected_qty)
        : item.current_qty != null && item.current_qty !== ""
          ? Number(item.current_qty)
          : 0;
  const unitCost = parseStocktakeUnitCost(
    item.current_cost_per_item,
    item.unit_cost,
    item.cost,
  );
  // Posted/void: keep API discrepancy_cost for Δ $ (do not recompute).
  const discrepancyCostRaw = item.discrepancy_cost ?? item.variance_cost;
  const discrepancyCostNum = Number(discrepancyCostRaw);
  const discrepancyCost =
    isPosted &&
      discrepancyCostRaw !== null &&
      discrepancyCostRaw !== undefined &&
      discrepancyCostRaw !== "" &&
      Number.isFinite(discrepancyCostNum)
      ? discrepancyCostNum
      : null;

  const displayNameRaw = pickString(
    item.current_product_name,
    item.product_name,
    item.product_title,
  );
  const isVariant = Boolean(item.variant_id && item.variant_id !== "0");
  const variantLabel = isVariant
    ? pickString(item.variant, item.variant_title, item.variant_name)
    : "";
  const name = displayNameRaw
    ? formatStocktakeProductDisplayName(displayNameRaw, displayNameRaw, {
      variantLabel,
      isVariant,
    })
    : null;

  return {
    stocktakeItemId: item.id != null ? String(item.id) : null,
    // Stored screen position; drives the list order on open.
    sortSeq,
    counted: counted == null || Number.isNaN(counted) ? null : counted,
    expected: Number.isNaN(expected) ? 0 : expected,
    note: item.note || "",
    reasonId: item.reason_id ?? "",
    // Prefer live product label from get_stocktake_details items.
    ...(name ? { name } : {}),
    // Posted/void: never surface recount flags in UI or exports.
    flag:
      isPosted || String(item.recount_flag) !== "1" ? null : "recount",
    ...(unitCost != null
      ? { currentCostPerItem: unitCost, cost: unitCost }
      : {}),
    ...(discrepancyCost != null ? { discrepancyCost } : {}),
  };
}

/** Build a standalone count row from a saved item not present in the catalog pool. */
function buildRowFromDetailItem(item, i = 0, isPosted = false) {
  const isVariant = Boolean(item.variant_id && item.variant_id !== "0");
  const productId = String(item.product_id);
  const variantRowId = isVariant ? String(item.variant_id) : productId;
  const varianceQty = Number(item.variance_qty ?? item.discrepancy);
  const varianceCost = Number(item.variance_cost ?? item.discrepancy_cost);
  const unitCost =
    parseStocktakeUnitCost(
      item.current_cost_per_item,
      item.unit_cost,
      item.cost,
    ) ??
    (varianceQty && !Number.isNaN(varianceCost)
      ? Math.abs(varianceCost / varianceQty)
      : 0);
  const variantLabel = isVariant
    ? pickString(item.variant, item.variant_title, item.variant_name)
    : "";
  const displayNameRaw = pickString(
    item.current_product_name,
    item.product_name,
    item.product_title,
  );
  const title =
    pickString(item.product_title) ||
    // current_product_name / product_name may be "Title / Variant" — keep base title.
    (displayNameRaw.includes(" / ")
      ? displayNameRaw.split(/\s*\/\s*/)[0]
      : displayNameRaw) ||
    "Untitled";
  const name = formatStocktakeProductDisplayName(displayNameRaw, title, {
    variantLabel,
    isVariant,
  });
  const patch = mapDetailItemToRowPatch(item, isPosted);

  return {
    id: variantRowId,
    productId,
    catalogProductId: productId,
    variantRowId,
    isVariant,
    variant: variantLabel,
    productTitle: title,
    upc: formatBarcodeSku(item.upc),
    sku: formatBarcodeSku(item.sku || item.product_sku || item.upc),
    customCode: pickCustomCode(
      item.custom_code,
      item.customCode,
      item.vcustom_code,
    ),
    name,
    category: String(item.category_id ?? ""),
    categoryId: String(item.category_id ?? ""),
    vendor: "",
    color: STOCKTAKE_COLORS[i % STOCKTAKE_COLORS.length],
    cost: unitCost,
    currentCostPerItem: unitCost,
    threshold: 0,
    addedAdHoc: false,
    // Posted/void: prefer API discrepancy_cost for Δ $ display.
    ...(isPosted && Number.isFinite(varianceCost)
      ? { discrepancyCost: varianceCost }
      : {}),
    ...patch,
  };
}

/**
 * Merge saved get_stocktake_details items into count rows.
 * Existing rows get counted_qty/note/stocktake_item_id; saved items missing
 * from the pool are appended as standalone rows.
 */
function applyStocktakeDetailsToRows(rows, items, isPosted = false) {
  if (!Array.isArray(items) || items.length === 0) return rows;

  const byKey = new Map();
  items.forEach((it) => {
    byKey.set(stocktakeRowMatchKey(it.product_id, it.variant_id), it);
  });

  const matchedKeys = new Set();
  const mergedRows = rows.map((r) => {
    const key = stocktakeRowMatchKey(
      r.productId,
      r.isVariant ? r.variantRowId : "",
    );
    const saved = byKey.get(key);
    if (!saved) return r;
    matchedKeys.add(key);
    return { ...r, ...mapDetailItemToRowPatch(saved, isPosted) };
  });

  const extraRows = items
    .filter(
      (it) =>
        !matchedKeys.has(stocktakeRowMatchKey(it.product_id, it.variant_id)),
    )
    .map((it, i) => buildRowFromDetailItem(it, rows.length + i, isPosted));

  return [...extraRows, ...mergedRows];
}

/**
 * Stable partition: counted rows first, pending after, newest count leading.
 * Used for rows with no stored sort_seq — see sortCountedRowsFirst.
 */
function sortCountedRowsByItemId(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const countedRows = [];
  const pendingRows = [];
  rows.forEach((row, index) => {
    (row.counted !== null && row.counted !== undefined
      ? countedRows
      : pendingRows
    ).push({ row, index });
  });
  if (countedRows.length === 0) return rows;

  countedRows.sort((a, b) =>
    compareByStocktakeItemIdDesc(
      parseStocktakeItemSortId(a.row.stocktakeItemId),
      parseStocktakeItemSortId(b.row.stocktakeItemId),
      a.index,
      b.index,
    ),
  );

  return [...countedRows, ...pendingRows].map(({ row }) => row);
}

/**
 * Order the session list the way the merchant left it.
 *
 * Rows carrying a stored sort_seq lead, in that order (0 = top) — that is the
 * exact list that was on screen at the last save, so a re-scanned or re-counted
 * row stays where the user put it instead of snapping back to insert order.
 *
 * Everything else follows: rows saved before sort_seq existed, plus catalog-pool
 * rows that were never saved at all. Those keep the previous rule — counted
 * first, newest stocktake_item_id leading, then pending in pool order — so a
 * stocktake created before this column opens exactly as it did before.
 */
function sortCountedRowsFirst(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;

  const placed = [];
  const unplaced = [];
  rows.forEach((row, index) => {
    const seq = parseStocktakeSortSeq(row.sortSeq);
    if (seq === null) unplaced.push({ row, index });
    else placed.push({ row, index, seq });
  });

  if (placed.length === 0) return sortCountedRowsByItemId(rows);

  placed.sort((a, b) => (a.seq !== b.seq ? a.seq - b.seq : a.index - b.index));

  return [
    ...placed.map(({ row }) => row),
    ...sortCountedRowsByItemId(unplaced.map(({ row }) => row)),
  ];
}

const stVariance = (row) => {
  if (row.counted === null || row.counted === undefined) return null;
  return row.counted - row.expected;
};

/** Max digits allowed in stocktake count text fields (max value 999999). */
const STOCKTAKE_COUNT_MAX_DIGITS = 6;
const STOCKTAKE_COUNT_MAX = 10 ** STOCKTAKE_COUNT_MAX_DIGITS - 1;

function clampStocktakeCount(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(Math.max(0, Math.trunc(n)), STOCKTAKE_COUNT_MAX);
}

/**
 * Sanitize count field text as the user types: strips everything except
 * digits (so @ # $ letters etc. never appear), caps at 6 digits and removes
 * leading zeros (a lone 0 stays).
 */
function sanitizeStocktakeCountInput(raw) {
  if (raw === null || raw === undefined) return "";
  const digits = String(raw)
    .replace(/\D/g, "")
    .slice(0, STOCKTAKE_COUNT_MAX_DIGITS);
  if (digits === "") return "";
  return digits.replace(/^0+(?=\d)/, "") || "0";
}

/** True when row was already saved in API `items` (has stocktake_item_id). */
function isSavedStocktakeListItem(row) {
  return (
    row?.stocktakeItemId != null && String(row.stocktakeItemId).trim() !== ""
  );
}

function isCategoryOrVendorStocktakeScope(scope) {
  const s = String(scope || "").toLowerCase();
  return s === "category" || s === "vendor";
}

/**
 * Category/Vendor only: emptied count text → "0" for items already in API
 * `items` (stocktakeItemId). Undrafted/pending pool rows stay blank.
 */
function sanitizeCountDraftForScope(raw, row, scope) {
  const sanitized = sanitizeStocktakeCountInput(raw);
  if (sanitized !== "") return sanitized;
  if (isCategoryOrVendorStocktakeScope(scope) && isSavedStocktakeListItem(row)) {
    return "0";
  }
  return "";
}

/**
 * Category/Vendor only: cleared counted qty → 0 for saved API `items` rows.
 * Other scopes / undrafted rows keep null (uncounted).
 */
function coerceClearedCountedQty(counted, row, scope) {
  if (counted !== null && counted !== undefined) {
    return clampStocktakeCount(counted);
  }
  if (isCategoryOrVendorStocktakeScope(scope) && isSavedStocktakeListItem(row)) {
    return 0;
  }
  return null;
}

/** Parse count field text: no leading zeros; lone 0 is allowed; max 6 digits. */
function parseStocktakeCountInput(raw) {
  if (raw === "" || raw == null) return null;
  const digits = String(raw).replace(/\D/g, "").slice(0, STOCKTAKE_COUNT_MAX_DIGITS);
  if (digits === "") return null;
  const normalized = digits.replace(/^0+(?=\d)/, "") || "0";
  const n = parseInt(normalized, 10);
  if (!Number.isFinite(n)) return null;
  return clampStocktakeCount(n);
}

/**
 * Overlay local count draft text onto session rows (no list reorder).
 * Used so header summary / progress / Variance Review reflect unsaved Web
 * count edits immediately (QV-7170).
 * Optional `scope`: Category/Vendor blank drafts on saved items → counted 0.
 * Blanking a previously counted drafted row sets `clearedCountPendingSave`
 * so lean walkaround/adhoc drafts can send blank counted_qty.
 */
function applyStocktakeCountDraftsToRows(rows, countDrafts, scope) {
  if (!Array.isArray(rows) || !countDrafts || typeof countDrafts !== "object") {
    return rows || [];
  }
  const draftIds = Object.keys(countDrafts);
  if (draftIds.length === 0) return rows;

  return rows.map((r) => {
    if (!Object.prototype.hasOwnProperty.call(countDrafts, r.id)) return r;
    const draft = sanitizeCountDraftForScope(countDrafts[r.id], r, scope);
    if (String(draft ?? "").trim() === "") {
      const wasCounted =
        r.counted !== null && r.counted !== undefined && r.counted !== "";
      const isSaved =
        r.stocktakeItemId != null && String(r.stocktakeItemId).trim() !== "";
      return {
        ...r,
        counted: null,
        ...(wasCounted && isSaved ? { clearedCountPendingSave: true } : null),
      };
    }
    const parsed = parseStocktakeCountInput(draft);
    if (parsed === null) return r;
    return { ...r, counted: parsed, clearedCountPendingSave: false };
  });
}

const stVarianceValue = (row) => {
  const v = stVariance(row);
  if (v === null) return 0;
  // Posted / void stocktakes: show API discrepancy_cost (signed by Δ units).
  const apiCost = row?.discrepancyCost;
  if (apiCost !== null && apiCost !== undefined && apiCost !== "") {
    const n = Number(apiCost);
    if (Number.isFinite(n)) {
      if (v === 0) return 0;
      return v < 0 ? -Math.abs(n) : Math.abs(n);
    }
  }
  return v * resolveStocktakeRowUnitCost(row);
};

function escapeStocktakeCsvCell(value) {
  if (value === null || value === undefined) return "";
  return `"${String(value).replace(/"/g, '""')}"`;
}

function downloadStocktakeCsv(filename, headers, dataRows) {
  const csvContent = [
    headers.join(","),
    ...dataRows.map((row) =>
      row.map((cell) => escapeStocktakeCsvCell(cell)).join(","),
    ),
  ].join("\n");
  // In a WebView an <a download>/blob silently fails, so hand the CSV to the
  // native app instead — same bridge the sales reports use. Returns false on
  // plain web, where the normal blob download below runs unchanged.
  if (exportRawCsvToNative(csvContent, filename)) return;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function getCountEntryStatus(row) {
  if (row.counted === null || row.counted === undefined) return "Pending";
  const v = stVariance(row);
  if (v === 0) return "Match";
  if (v < 0) return "Loss";
  return "Gain";
}

function buildStocktakeExportFilename(session, suffix) {
  const base =
    String(session?.name || session?.reference || "stocktake")
      .trim()
      .replace(/[^\w\-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "stocktake";
  const date = new Intl.DateTimeFormat("en-CA").format(new Date());
  return `${base}_${suffix}_${date}.csv`;
}

function exportStocktakeCountEntries(rows, session, { hideCost = false } = {}) {
  const hideRecount = isPostedOrClosedStocktakeStatus(session?.status);
  const headers = [
    "Item",
    "SKU",
    "Expected",
    "Counted",
    "Variance Units",
    ...(hideCost ? [] : ["Variance $"]),
    "Status",
    "Reason",
    "Note",
    ...(hideRecount ? [] : ["Flagged For Recount"]),
  ];
  const dataRows = rows.map((row) => {
    const v = stVariance(row);
    const vv = stVarianceValue(row);
    return [
      row.name,
      row.sku,
      row.expected,
      row.counted === null || row.counted === undefined ? "" : row.counted,
      v === null ? "" : v,
      ...(hideCost
        ? []
        : [
          row.counted === null || row.counted === undefined
            ? ""
            : vv.toFixed(2),
        ]),
      getCountEntryStatus(row),
      resolveStocktakeReasonLabel(row),
      row.note || "",
      ...(hideRecount
        ? []
        : [row.flag === "recount" ? "Yes" : ""]),
    ];
  });
  downloadStocktakeCsv(
    buildStocktakeExportFilename(session, "count_entries"),
    headers,
    dataRows,
  );
}

function exportStocktakeVarianceReview(rows, session, { hideCost = false } = {}) {
  const hideRecount = isPostedOrClosedStocktakeStatus(session?.status);
  const headers = [
    "Item",
    "SKU",
    "Expected",
    "Counted",
    "Variance Units",
    ...(hideCost ? [] : ["Variance $"]),
    "Reason",
    "Note",
    ...(hideRecount ? [] : ["Flagged For Recount"]),
  ];
  const dataRows = rows
    .filter((row) => row.counted !== null && stVariance(row) !== 0)
    .sort((a, b) =>
      hideCost
        ? Math.abs(stVariance(b)) - Math.abs(stVariance(a))
        : Math.abs(stVarianceValue(b)) - Math.abs(stVarianceValue(a)),
    )
    .map((row) => {
      const v = stVariance(row);
      const vv = stVarianceValue(row);
      return [
        row.name,
        row.sku,
        row.expected,
        row.counted,
        v,
        ...(hideCost ? [] : [vv.toFixed(2)]),
        resolveStocktakeReasonLabel(row),
        row.note || "",
        ...(hideRecount
          ? []
          : [row.flag === "recount" ? "Yes" : ""]),
      ];
    });
  if (dataRows.length === 0) return false;
  downloadStocktakeCsv(
    buildStocktakeExportFilename(session, "variance_review"),
    headers,
    dataRows,
  );
  return true;
}

/** Remove a product/variant from stocktake.all_items shape after delete. */
export function removeProductFromAllItems(allItems, row) {
  if (!Array.isArray(allItems) || !row) return allItems;

  const productId = String(row.productId ?? "").trim();
  const variantId = row.isVariant ? String(row.variantRowId ?? "").trim() : "";
  if (!productId) return allItems;

  return allItems.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const entryProductId = Object.keys(entry)[0];
    if (String(entryProductId).trim() !== productId) return [entry];

    const csv = entry[entryProductId];
    if (!variantId || csv == null || String(csv).trim() === "") return [];

    const remaining = String(csv)
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v && v !== variantId);
    if (remaining.length === 0) return [];
    return [{ [entryProductId]: remaining.join(",") }];
  });
}
/** Map UI count row → remove_stocktake_item API fields. */
function mapRowToRemoveStocktakeItem(row) {
  return {
    product_id: String(row.productId ?? "").trim(),
    variant_id: row.isVariant ? String(row.variantRowId ?? "").trim() : "",
  };
}

/**
 * Remove a product/variant line from a stocktake session.
 * POST StocktakeNewUI/remove_stocktake_item (multipart/form-data)
 */
export async function removeStocktakeSessionItem({
  merchantId,
  tokenId,
  loginType,
  sessionId,
  loginEmployeeId,
  token,
  stocktakeId,
  productId,
  variantId,
  row,
}) {
  const fromRow = row ? mapRowToRemoveStocktakeItem(row) : null;
  const resolvedProductId = String(
    productId ?? fromRow?.product_id ?? "",
  ).trim();
  const resolvedVariantId = String(
    variantId ?? fromRow?.variant_id ?? "",
  ).trim();

  if (!merchantId || !token || !stocktakeId || !resolvedProductId) {
    return { status: false, message: "Missing required fields." };
  }

  try {
    const formData = new FormData();
    formData.append("merchant_id", String(merchantId).trim());
    formData.append("stocktake_id", String(stocktakeId).trim());
    formData.append("product_id", resolvedProductId);
    formData.append("variant_id", resolvedVariantId);
    appendStocktakeAuthToFormData(formData, {
      tokenId,
      loginType,
      sessionId,
      loginEmployeeId,
    });

    const response = await axios.post(
      BASE_NODE_URL + STOCKTAKE_REMOVE_ITEM,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return { httpStatus: response.status, data: response.data };
  } catch (error) {
    console.error("removeStocktakeSessionItem", error);
    throw error;
  }
}

export {
  ST_STATUS_META,
  ST_SCOPE_META,
  normalizeStocktakeStatus,
  mapListFilterToApiStatus,
  isPostedOrClosedStocktakeStatus,
  formatStocktakeListProgress,
  stocktakeListHidesProgressBar,
  canVoidStocktakeStatus,
  canEmailStocktakeStatus,
  stFmtMoney,
  stFmtSigned,
  resolveStocktakeRowUnitCost,
  formatStocktakeVarianceCostFormula,
  computeStocktakeDiscrepancyTotals,
  buildTotalDataProductsRequestBody,
  getSessionProductFilters,
  getSessionCatalogSearchFilters,
  getProductsPool,
  fetchScopedProductsPoolPage,
  fetchStocktakeProductByScan,
  mapTotalDataProductRow,
  getCatalogProductLineKey,
  getCountRowLineKey,
  getStocktakeLineKey,
  countRowMatchesCatalogProduct,
  findCountRowForCatalogProduct,
  moveCountRowToTopOfCounted,
  moveCountRowsToTopOfCounted,
  assignStocktakeSortSeq,
  buildStocktakeSavePayload,
  orderCountDraftRowIds,
  countRowsMatchSameLine,
  buildAdHocCountRowFromCatalog,
  canRemoveLocalStocktakeRow,
  canShowStocktakeRowRemove,
  resolveSessionItemLimit,
  stBuildCountRows,
  mapRowToSaveCountItem,
  mapCountedRowsToSaveCountItems,
  mapLeanDraftRowsToSaveCountItems,
  mapWalkaroundDraftRowsToSaveCountItems,
  mapRowsToSaveCountItems,
  saveStocktakeCounts,
  getStocktakeDetails,
  postStocktakeAdjustment,
  voidStocktake,
  deleteStocktake,
  emailStocktake,
  updateStocktakeRecountFlag,
  applyStocktakeDetailsToRows,
  sortCountedRowsFirst,
  buildRowFromDetailItem,
  countRowMatchesSearch,
  countRowMatchesExactScanCode,
  findCountRowForExactScanCode,
  catalogProductMatchesExactScanCode,
  isStocktakeRowUncounted,
  pickNearestPendingRowIdAfterLeave,
  resolveVisibleRowIndexAfterFilterLeave,
  stampStocktakeRowCustomCode,
  countRowMatchesCatalogSearchHits,
  buildStocktakeItemFilter,
  filterPoolByStocktakeItems,
  isRichAllItemsEntry,
  dedupeRichStocktakeAllItems,
  remapGhostParentDetailItems,
  enrichDetailItemsProductNamesFromAllItems,
  buildRowFromAllItemsEntry,
  formatBarcodeSku,
  parseStocktakeCountInput,
  sanitizeStocktakeCountInput,
  sanitizeCountDraftForScope,
  coerceClearedCountedQty,
  clampStocktakeCount,
  applyStocktakeCountDraftsToRows,
  STOCKTAKE_COUNT_MAX_DIGITS,
  exportStocktakeCountEntries,
  exportStocktakeVarianceReview,
  resolveStocktakeReasonLabel,
  getCountEntryStatus,
  stVariance,
  stVarianceValue,
  stocktakeAuthFields,
  withStocktakeAuth,
};
